import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createOffer, getOffer, getOrderClients, offerToOrder, updateOffer } from '../services/orders';
import ClientPicker from '../components/common/ClientPicker';
import OfferPdfModal from '../components/Offers/OfferPdfModal';
import DocumentPreviewModal from '../components/common/DocumentPreviewModal';
import './OfferEditorPage.css';

/** LAKPED-16.3 — el editor de la oferta: una línea por artículo.
 *
 *  Lo único obligatorio es el precio. El importe de cada línea y el total los calcula también el
 *  servidor al guardar, así que lo que se ve aquí y lo que queda guardado no pueden discrepar.
 */
const PASOS = ['Documento', 'Histórico', 'Oferta', 'PDF y envío'];

const NOMBRE_ESTADO = {
  draft: 'Borrador', sent: 'Enviada', accepted: 'Aceptada', rejected: 'Rechazada', expired: 'Caducada',
};

const LINEA_VACIA = { part_code: '', description: '', quantity: '', unit_price: '', price_source: '' };

const numero = (valor) => (valor === '' || valor === null || valor === undefined ? null : Number(valor));
const euros = (valor) => (valor || valor === 0
  ? valor.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })
  : '—');

const importeDe = (linea) => {
  const precio = numero(linea.unit_price);
  if (precio === null || Number.isNaN(precio)) return null;
  const cantidad = numero(linea.quantity);
  return precio * (cantidad === null || Number.isNaN(cantidad) ? 1 : cantidad);
};

const OfferEditorPage = () => {
  const navigate = useNavigate();
  const { offerHash } = useParams();
  const [searchParams] = useSearchParams();
  const analysisHash = searchParams.get('analysis');

  // LAKPED-21: la oferta nueva llega con lo leído en la portada (cliente, nº de pedido, pieza y
  // cantidad) y, si se eligió una base, con su descripción y su precio.
  const [cabecera, setCabecera] = useState({
    client: searchParams.get('cliente') || '',
    reference: searchParams.get('referencia') || '',
    notes: '',
  });
  const [lineas, setLineas] = useState([{
    ...LINEA_VACIA,
    part_code: searchParams.get('pieza') || '',
    description: searchParams.get('descripcion') || '',
    quantity: searchParams.get('cantidad') || '',
    unit_price: searchParams.get('precio') || '',
    price_source: searchParams.get('origen') || '',
  }]);
  const [clientes, setClientes] = useState([]);
  const [oferta, setOferta] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [guardada, setGuardada] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  // `verPdf`: false, 'ver' o 'imprimir' (Descargar PDF abre el pop-up y lanza la impresión ahí mismo).
  const [verPdf, setVerPdf] = useState(false);
  const [verDocumentoOrigen, setVerDocumentoOrigen] = useState(false);

  useEffect(() => {
    getOrderClients().then(setClientes).catch(() => setClientes([]));
  }, []);

  const cargar = useCallback(async () => {
    if (!offerHash) return;
    try {
      // LAKPED-28: la pantalla de oferta es siempre esta, también para las hechas con la calculadora.
      const data = await getOffer(offerHash);
      setOferta(data);
      setCabecera({ client: data.client?.name || '', reference: data.reference || '', notes: data.notes || '' });
      setLineas((data.lines || []).length
        ? data.lines.map(l => ({
            part_code: l.part_code || '',
            description: l.description || '',
            quantity: l.quantity ?? '',
            unit_price: l.unit_price ?? '',
            price_source: l.price_source || '',
          }))
        : [{ ...LINEA_VACIA }]);
    } catch (err) {
      setError('No se ha podido cargar la oferta.');
    }
  }, [offerHash]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarLinea = (indice, campo) => (e) => {
    const valor = e.target.value;
    setLineas(prev => prev.map((l, i) => (i === indice ? { ...l, [campo]: valor } : l)));
  };

  const quitarLinea = (indice) => setLineas(prev => (
    prev.length === 1 ? [{ ...LINEA_VACIA }] : prev.filter((_, i) => i !== indice)
  ));

  const total = lineas.reduce((suma, l) => suma + (importeDe(l) || 0), 0);
  const algunPrecio = lineas.some(l => importeDe(l) !== null);

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    const cuerpo = {
      reference: cabecera.reference || null,
      notes: cabecera.notes || null,
      lines: lineas
        .filter(l => l.part_code || l.description || l.unit_price !== '')
        .map(l => ({
          part_code: l.part_code || null,
          description: l.description || null,
          quantity: numero(l.quantity),
          unit_price: numero(l.unit_price),
          price_source: l.price_source || null,
        })),
    };
    try {
      if (offerHash) {
        await updateOffer(offerHash, cuerpo);
        setGuardada(true);
      } else {
        const creada = await createOffer({
          ...cuerpo,
          analysis_hash: analysisHash || null,
          client_name: cabecera.client || null,
        });
        navigate(`/offers/${creada.hash}`, { replace: true });
      }
    } catch (err) {
      setError('No se ha podido guardar la oferta.');
    } finally {
      setGuardando(false);
    }
  };

  // LAKPED-22 / LAKPED-24: enviada y aceptada se marcan desde aquí, sin pasar por el PDF.
  const cambiarEstado = async (status) => {
    setCambiandoEstado(true);
    setError(null);
    try {
      setOferta(await updateOffer(offerHash, { status }));
    } catch (err) {
      setError('No se ha podido cambiar el estado de la oferta.');
    } finally {
      setCambiandoEstado(false);
    }
  };

  // LAKPED-25: el pedido nace de la oferta con su cliente y sus líneas; el documento del cliente
  // se le adjunta después, desde el propio pedido. Repetirlo no duplica: lleva al pedido que ya hay.
  const pasarAPedido = async () => {
    setCambiandoEstado(true);
    try {
      const pedido = await offerToOrder(offerHash);
      navigate(`/orders/${pedido.hash}`);
    } catch (err) {
      setError('No se ha podido pasar la oferta a pedido.');
      setCambiandoEstado(false);
    }
  };

  const estado = oferta?.status || 'draft';
  const ocupado = guardando || cambiandoEstado;

  // LAKPED-26 / LAKPED-27: desde la oferta se vuelve al documento de origen y al histórico de la pieza.
  const hashDocumento = oferta?.analysis?.hash || analysisHash;
  // Vuelta cronológica: a la pantalla de entrada con este documento ya cargado.
  const verDocumento = () => navigate(`/similarity-search?documento=${hashDocumento}`);
  const alHistorico = () => {
    const query = new URLSearchParams({ analysis: hashDocumento || '' });
    if (cabecera.client) query.set('cliente', cabecera.client);
    const codigo = lineas.find(l => l.part_code)?.part_code;
    if (codigo) query.set('codigo', codigo);
    navigate(`/offers/history?${query.toString()}`);
  };

  return (
    <div className="offer-editor">
      {/* LAKPED-26/27: los pasos ya hechos son el camino de vuelta (Paula: "pulsar aquí y volver"). */}
      <ol className="offer-editor-pasos">
        {PASOS.map((titulo, i) => (
          <li key={titulo} className={i === 2 ? 'actual' : (i < 2 ? 'hecho' : '')}>
            {i < 2 ? (
              <button type="button" onClick={i === 0 ? verDocumento : alHistorico} disabled={i === 0 && !hashDocumento}>
                <span className="offer-editor-paso-num">✓</span>
                {titulo}
              </button>
            ) : (
              <>
                <span className="offer-editor-paso-num">{i + 1}</span>
                {titulo}
              </>
            )}
          </li>
        ))}
      </ol>

      <div className="offer-editor-cabecera">
        <div>
          <h1>
            {oferta ? `Oferta ${oferta.reference || oferta.hash.slice(0, 8)}` : 'Nueva oferta'}
            <span className="offer-editor-estado">{NOMBRE_ESTADO[estado] || estado}</span>
          </h1>
          <p className="offer-editor-sub">Una línea por artículo. Lo único obligatorio es el precio.</p>
        </div>
        <div className="offer-editor-acciones">
          <button type="button" className="offer-editor-principal" onClick={() => navigate('/offers')}>
            Ver todas las ofertas
          </button>
          <button type="button" className="offer-editor-principal" onClick={guardar} disabled={ocupado}>
            {guardando ? 'Guardando…' : 'Guardar borrador'}
          </button>
          {/* Todos los botones a la vista desde el principio (lo pidió Paula): los que necesitan la
              oferta guardada van desactivados hasta que se guarda el borrador. */}
          <button type="button" className="offer-editor-principal" onClick={() => setVerPdf('ver')} disabled={!oferta}>
            Vista previa del PDF
          </button>
          <button type="button" className="offer-editor-principal" onClick={() => setVerPdf('imprimir')} disabled={!oferta}>
            Descargar PDF
          </button>
          {estado === 'draft' && (
            <button type="button" className="offer-editor-principal" onClick={() => cambiarEstado('sent')} disabled={!oferta || ocupado}>
              Marcar como enviada
            </button>
          )}
          {['draft', 'sent'].includes(estado) && (
            <button type="button" className="offer-editor-principal" onClick={() => cambiarEstado('accepted')} disabled={!oferta || ocupado}>
              Aceptar oferta
            </button>
          )}
          {oferta?.order_hash ? (
            <button type="button" className="offer-editor-principal" onClick={() => navigate(`/orders/${oferta.order_hash}`)}>
              Ver el pedido
            </button>
          ) : ['draft', 'sent', 'accepted'].includes(estado) && (
            <button type="button" className="offer-editor-principal" onClick={pasarAPedido} disabled={!oferta || ocupado}>
              Pasar a pedido
            </button>
          )}
          {!oferta && <span className="offer-editor-aviso-guardar">Guarda el borrador para activar el resto.</span>}
        </div>
      </div>

      {error && <p className="offer-editor-error">{error}</p>}
      {guardada && <p className="offer-editor-ok">Guardada.</p>}

      {verDocumentoOrigen && hashDocumento && (
        <DocumentPreviewModal
          analysisHash={hashDocumento}
          filename={oferta?.analysis?.filename || 'Documento de origen'}
          onClose={() => setVerDocumentoOrigen(false)}
        />
      )}
      {verPdf && oferta && <OfferPdfModal oferta={oferta} imprimir={verPdf === 'imprimir'} onClose={() => setVerPdf(false)} />}

      <div className="offer-editor-tarjeta">
        <h2>Datos</h2>
        <div className="offer-editor-datos">
          <label>
            Cliente
            <ClientPicker
              value={cabecera.client}
              clients={clientes}
              onChange={(valor) => setCabecera(prev => ({ ...prev, client: valor }))}
            />
          </label>
          <label>
            Referencia de la petición del cliente
            <input
              value={cabecera.reference}
              onChange={(e) => setCabecera(prev => ({ ...prev, reference: e.target.value }))}
              placeholder="RFQ-26-114"
            />
          </label>
        </div>
        {hashDocumento && (
          <p className="offer-editor-adjunto">
            Lleva enganchado el documento de origen{oferta?.analysis?.filename ? ` (${oferta.analysis.filename})` : ''}.{' '}
            <button type="button" className="offer-editor-enlace" onClick={() => setVerDocumentoOrigen(true)}>Ver el documento</button>
          </p>
        )}
      </div>

      <div className="offer-editor-tarjeta">
        <h2>Líneas de la oferta</h2>
        <table className="offer-editor-tabla">
          <thead>
            <tr>
              <th>Artículo</th>
              <th>Descripción</th>
              <th className="der">Cant.</th>
              <th className="der">Precio ud. (€)</th>
              <th className="der">Importe</th>
              <th>Referencia de precio</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lineas.map((linea, i) => (
              <tr key={i}>
                <td><input value={linea.part_code} onChange={cambiarLinea(i, 'part_code')} /></td>
                <td><input value={linea.description} onChange={cambiarLinea(i, 'description')} /></td>
                <td className="der"><input className="corto" type="number" min="0" value={linea.quantity} onChange={cambiarLinea(i, 'quantity')} /></td>
                <td className="der"><input className="corto" type="number" min="0" step="0.01" value={linea.unit_price} onChange={cambiarLinea(i, 'unit_price')} /></td>
                <td className="der">{euros(importeDe(linea))}</td>
                <td>
                  <input
                    value={linea.price_source}
                    onChange={cambiarLinea(i, 'price_source')}
                    placeholder="de dónde sale el precio"
                  />
                </td>
                <td>
                  <button type="button" className="offer-editor-quitar" onClick={() => quitarLinea(i)} title="Quitar la línea">
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="offer-editor-pie-tabla">
          <button type="button" className="offer-editor-secundario" onClick={() => setLineas(prev => [...prev, { ...LINEA_VACIA }])}>
            + Añadir línea
          </button>
          <span className="offer-editor-total">
            Total (sin IVA) <b>{algunPrecio ? euros(total) : '—'}</b>
          </span>
        </div>
      </div>

      <div className="offer-editor-tarjeta">
        <h2>Condiciones</h2>
        <p className="offer-editor-ayuda">Plazo de entrega y observaciones. Aparecen en el PDF.</p>
        <textarea
          rows={3}
          value={cabecera.notes}
          onChange={(e) => setCabecera(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Plazo de entrega: 2 semanas desde recepción del material. Validez de la oferta: 30 días."
        />
      </div>

    </div>
  );
};

export default OfferEditorPage;
